import React, { useEffect, useState } from 'react';
import { Container, InfoWrapper, StyledLink, Title, StageLabel } from './styles';
import { useTranslation } from 'react-i18next';
import { StepperSelectors, StepperOperations } from '../../ducks/stepper';
import { useDispatch, useSelector } from 'react-redux';
import { stageConfig } from '../Stepper/stageConfig';
import { getStageIdByStepId, getStepInfo } from '../../utils/stageNavigationUtils';
import { getDevice } from '@panel/commons/helpers';
import { Modal } from '@panel/commons';
import { SaveChangeForm } from '../modals/SaveChangeForm';
import { SavePostingOperations, SavePostingSelectors } from '../../ducks/savePosting';
import { triggerSources } from '../../constants/triggerSources';
import { useHistory } from 'react-router-dom';
import { FormGuardOperations } from '../../ducks/formGuard';
import adaptStepDataAndSendTrackinInfo, { sendModalTrackingInfo } from '../../utils/adaptStepDataAndSendTrackinInfo';
import { setCookieWithTTL } from '@panel/commons/helpers/cookieHelper';
import { lastStepGuardHelper } from '../../utils/lastStepGuardHelper';

const StepHeader = ({
  showSaveAndExitButton = true,
  onSubmit = () => {},
  id = '',
  isMobile = false,
  getIsValidForm,
  isPlanEmpty = false,
  mainData = {},
  isEdition = false,
  postingId = '',
  planName = '',
  isDraftFromPanel = false,
  selectedOperationType = '',
  selectedPropertyType = '',
  selectedPropertySubType = ''
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { currentStep, lastStep } = useSelector(StepperSelectors.getStepperState);
  const currentStepInfo = getStepInfo(currentStep);
  const [showModal, setShowModal] = useState(false);
  const [impressionDlSent, setImpressionDlSent] = useState(false);
  const [isValidForm, setIsValidForm] = useState(true);
  const { triggerSource, successOnSave, errorOnSave } = useSelector(SavePostingSelectors.getSavePostingState);
  const envWithUncheckLastStepAvailable = ['producto-avisos', 'producto1', 'prepro'].includes(
    process.env.REACT_APP_PANEL_ENVIRONMENT
  );
  const handleRedirect = () => {
    setCookieWithTTL('postingSavedMessage', 'professionalPubli.formGuardModal.saveAndExit.snackbar');
    history.push('/avisos');
  };

  useEffect(() => {
    if (lastStep && !envWithUncheckLastStepAvailable) {
      if (!isEdition && currentStep !== 1) {
        const locationPath = history.location.pathname;
        lastStepGuardHelper(lastStep, postingId, history, [], locationPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, lastStep, currentStepInfo]);

  useEffect(() => {
    if (isPlanEmpty && mainData?.formValues && currentStepInfo.stepId === 8 && !impressionDlSent) {
      adaptStepDataAndSendTrackinInfo(
        currentStepInfo.stepId,
        postingId,
        'Impresion modal sin planes',
        mainData.formValues.propertyTypeName,
        mainData.formValues.operationType,
        isEdition,
        '',
        isDraftFromPanel,
        isPlanEmpty,
        mainData.formValues.propertySubTypeName
      );
      setImpressionDlSent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlanEmpty, mainData, currentStepInfo, impressionDlSent, postingId, isEdition]);

  useEffect(() => {
    if (successOnSave) {
      if (triggerSource === triggerSources.SAVE_AND_EXIT) {
        dispatch(SavePostingOperations.clearSaveState());
        handleRedirect();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successOnSave, triggerSource]);

  useEffect(() => {
    if (errorOnSave && triggerSource === triggerSources.SAVE_AND_EXIT) {
      dispatch(
        StepperOperations.setSnackAction({
          action: 'savePostingError',
          result: 'ERROR',
          component: t('professionalPubli.main.error.savePostingErrror'),
          section: 'Publicador profesionales - Save Posting',
          preventDuplicateActions: true,
          preventStacking: true
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorOnSave, triggerSource]);

  const handleSaveAndExit = isValidForm => {
    setShowModal(false);
    setIsValidForm(null);
    setCookieWithTTL('redirectFromSaveAndExitModal', true);
    dispatch(FormGuardOperations.setDirtyForm(false));
    setTimeout(() => {
      // Agregamos un timeout para esperar que se actualice el estado dirtyForm para evitar que se muestre el modal de confirmación
      if (!isValidForm) {
        return onExitWithoutSaving();
      }
      onHandleSubmit();
    }, 0);
  };

  const onExitWithoutSaving = () => {
    if (isPlanEmpty) {
      setCookieWithTTL('postingSavedMessage', 'panel.fromPublisherProf.draft.snackbar.SUCCESS');
    }
    // se agrega un timeout de 1 segundo para que el guard del form en redireccionamiento fuera del publi encuentre
    // el estado dirtyForm actualizado a false
    setTimeout(() => {
      history.push('/avisos');
      setCookieWithTTL('postingSavedMessage', 'professionalPubli.formGuardModal.saveAndExit.snackbar');
    }, 1000);
  };

  function onHandleSubmit() {
    dispatch(SavePostingOperations.setSaveSource(triggerSources.SAVE_AND_EXIT));
    // Se evita onSubmit en el paso 8 para que no publique el aviso cuando queremos salir.
    if (currentStep !== 8) {
      onSubmit();
    }
    if (currentStep === 8) {
      setCookieWithTTL('postingSavedMessage', 'panel.fromPublisherProf.draft.snackbar.SUCCESS');
      history.push('/avisos');
    }
  }

  useEffect(() => {
    if (showModal) {
      setModalText();
      sendModalTrackingInfo('impression_modal', 'Modal guardar y salir');
    } else {
      setIsValidForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const setModalText = async () => {
    const isValid = await getIsValidForm();
    setIsValidForm(isValid);
  };

  return (
    <Container device={getDevice()}>
      <InfoWrapper device={getDevice()} data-publi='publi-infoWrapper'>
        <StageLabel>
          {t('professionalPubli.header.stage', { value1: getStageIdByStepId(currentStep), value2: stageConfig.length })}
        </StageLabel>
        <Title>{t(`professionalPubli.${currentStepInfo?.title}.header`)}</Title>
      </InfoWrapper>
      {showSaveAndExitButton && (
        <StyledLink
          data-publi='header-button-saveAndExit'
          onClick={() => {
            setShowModal(true);
            adaptStepDataAndSendTrackinInfo(
              currentStepInfo.stepId,
              postingId,
              'Guardar y salir',
              selectedPropertyType || mainData.formValues.propertyTypeName,
              selectedOperationType || mainData.formValues.operationType,
              isEdition,
              planName,
              isDraftFromPanel,
              isPlanEmpty,
              selectedPropertySubType || mainData.formValues.propertySubTypeName
            );
          }}
        >
          {t('professionalPubli.cta.save')}
        </StyledLink>
      )}
      {showModal && (
        <Modal>
          <SaveChangeForm
            bottomModal={isMobile}
            data-publi={`saveChangeModal-${id}`}
            openModal={showModal}
            onClose={() => {
              setShowModal(false);
            }}
            handleRedirect={() => {
              sendModalTrackingInfo('click_modal', 'Modal guardar y salir', 'Guardar y salir');
              handleSaveAndExit(isValidForm);
            }}
            handleKeepEditing={() => {
              setShowModal(false);
              sendModalTrackingInfo('click_modal', 'Modal guardar y salir', 'Continuar editando');
            }}
            title={t(
              `professionalPubli.formGuardModal.saveAndExit${
                isValidForm && currentStepInfo.stepId !== 8 ? '' : '.withFormErrors'
              }.title`
            )}
            description={
              currentStepInfo.stepId === 8
                ? t('professionalPubli.formGuardModal.saveAndExit.withFormErrors.description.lastStep')
                : t(`professionalPubli.formGuardModal.saveAndExit${isValidForm ? '' : '.withFormErrors'}.description`)
            }
            exitButtonText={t('professionalPubli.formGuardModal.saveAndExit.exit')}
            keepButtonText={t('professionalPubli.formGuardModal.keep')}
          />
        </Modal>
      )}
    </Container>
  );
};

export default StepHeader;
